import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FileTypes,
  FileUpload,
  FileUploadProps,
} from 'src/features/common/components/file-upload/file-upload';
import {
  ApplicationAction,
  ApplicationActionType,
  DataStatus,
  ForeignNationalType,
  HeadingLayout,
  HeadingOne,
  HeadingTwo,
  HelpButton,
  IdentityTypeDirectors,
  PageLayout,
  SolutionType,
  stripWhitespace,
  useWizardEffect,
} from 'src/features/common';
import { ApplicationContext } from 'src/features/onboarding/context';
import { OnboardingDocument } from 'src/features/common/types/application/onboarding-document';
import { CompanyType } from 'src/features/common/types/application/business';
import { getDocumentApiFromOnboardingDocument } from 'src/features/common/helpers/dto-mappers/salesforce/get-document-api-from-onboarding-document';
import { useAsyncDispatch } from 'src/features/order/hooks/use-async-dispatch';
import { getApplicationAPIFromApplication } from 'src/features/common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { ApplicationAPI } from 'src/features/common/types/salesforce/api/application/application-api';
import { StepNames } from 'src/features/common/helpers/dto-mappers/salesforce/step-names';
import { PAGE_NAME } from 'src/features/common/types/tealium-service';
import { uploadDocumentLabelChanges } from 'src/features/onboarding/helpers/utils';
import OnboardingTealiumService from 'src/features/onboarding/services/onboarding-tealium-services';
import { getBusinessTypeDirectorLookup } from '../director-details/resources/business-type-to-director-lookup';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { ImageService } from 'src/services/image-join-service';
import { Environment } from 'src/Environment';
import useImageUpload from 'src/features/onboarding/hooks/use-image-upload';
import { ImageUploadActionType } from 'src/features/onboarding/types/image-upload-reducer-actions';
import './upload-documents.scss';

export type DocumentWithOptionalBody = OnboardingDocument & {
  documentBody?: string;
  documentUrl?: string;
  initial?: boolean;
  unconfirmed?: boolean;
};

export type IdItem = {
  idType: IdentityTypeDirectors;
  idNumber: string;
};

export type UploadError = {
  error: boolean;
  message: string;
};

export type UploadDocumentError = {
  identityDocument: UploadError;
  workPermit: UploadError;
  permanentRes: UploadError;
  proofOfBanking: UploadError;
  resLetter: UploadError;
  refugeeLetter: UploadError;
  asylumSeeker: UploadError;
  npoFoundingDoc: UploadError;
  trustResLetter: UploadError;
  trustDeed: UploadError;
};

export enum OnboardingDocumentTypeEnum {
  ID_TYPE = 'ID',
  BANK_PROOF = 'Proof of bank account letter',
  RES_LETTER = 'Resolution letter',
  NPO_FOUNDING_DOC = 'NPO founding documentation',
  TRUST_DEED = 'Trust deed',
  TRUST_RES_LETTER = 'Resolution letter',
  PROOF_OF_RESIDENCE = 'Proof of residence',
  ID_BOOK = 'Old Green Identity Document',
  ID_CARD = 'New Identity Card',
  PASSPORT = 'Passport',
  WORK_PERMIT = 'Work Permit',
  PERMANENT_RESIDENCY = 'Permanent Residency',
  FOREIGN_NATIONAL_ID_DOC = 'Foreign National ID Document',
  REFUGEE_LETTER = 'Refugee Letter',
  ASYLUM_SEEKER = 'Asylum Seeker',
}

type DocumentConfig = {
  props: FileUploadProps;
  showOnlyIf?: () => boolean;
};

export type IDDocsUploaded = {
  idNumber: string;
  numOfSmartCards: number;
  numOfGreenBooks: number;
  numOfPassports: number;
};

export const UploadDocuments = () => {
  const NON_ID_SIZE_LIMIT = 3_072_000;
  const ID_SIZE_LIMIT = 1_024_000;

  const [uploadErrors, setUploadErrors] = useState<UploadDocumentError>({
    identityDocument: { error: false, message: '' },
    workPermit: { error: false, message: '' },
    permanentRes: { error: false, message: '' },
    proofOfBanking: { error: false, message: '' },
    resLetter: { error: false, message: '' },
    refugeeLetter: { error: false, message: '' },
    asylumSeeker: { error: false, message: '' },
    npoFoundingDoc: { error: false, message: '' },
    trustResLetter: { error: false, message: '' },
    trustDeed: { error: false, message: '' },
  });
  const { application, dispatch, salesforceService, catalogue } = useContext(ApplicationContext);
  const dispatchAsync = useAsyncDispatch(application, dispatch);

  const { imageUploadState, imageUploadDispatch } = useImageUpload();

  const isThirdPartyJourney = useMemo(
    (): boolean => application.solutionType === SolutionType.thirdPartyOnboarding,
    [application.solutionType],
  );

  const isDpo = useMemo((): boolean => !Environment.current.disabledFeatures.dpo, []);

  const inMiniApp = useMemo((): boolean => MiniAppEvents.isInMiniApp(), []);

  const labels = uploadDocumentLabelChanges(
    application.solutionType === SolutionType.thirdPartyOnboarding,
  );

  const isPendingApp = useMemo(
    (): boolean => !!application.pendingApp?.isPending,
    [application.pendingApp],
  );

  const foreignNationalsEnabled = useMemo(
    (): boolean => !Environment.current?.disabledFeatures?.passport || false,
    [],
  );

  const imageJoinEnabled = useMemo(
    (): boolean => !Environment.current.disabledFeatures.imageJoin,
    [],
  );

  const taggingJourney = useMemo(() => {
    switch (true) {
      case !!application.pendingApp?.isPending:
        return 'pending app';

      case !!application.brokerLogin || application.agentId:
        return 'third party';

      case application.deviceActivationTagging:
        return 'register device';

      default:
        return 'onboarding';
    }
  }, []);

  const { setNextButtonEnabled, setOnNextEventHandler, setLoadingOverlayVisible } = useWizardEffect(
    () => ({ softDisable: true }),
    [application],
  );

  const companyTypeApplication = application.businessDetails?.companyType;

  const companyType = // @ts-ignore
    companyTypeApplication !== undefined ? companyTypeApplication : CompanyType.SOLE_PROP;

  const getIDsAndPassports = (): IdItem[] => {
    const ids: IdItem[] = (application.directors?.directors || [])
      .filter(
        ({ idType }) =>
          idType === IdentityTypeDirectors.RSA_ID || idType === IdentityTypeDirectors.PASSPORT,
      )
      .map(({ idNumber, idType }) => ({
        idType: idType || IdentityTypeDirectors.RSA_ID,
        idNumber: idNumber || '',
      }));

    if (
      // @ts-ignore
      application.contact?.identityType === 'RSA ID' ||
      application.contact?.identityType === IdentityTypeDirectors.RSA_ID ||
      application.contact?.identityType === IdentityTypeDirectors.PASSPORT
    ) {
      ids.push({
        idType: application.contact.identityType,
        idNumber: application.contact.idNumber || '',
      });
    }

    return Array.from(new Set(ids));
  };

  const getForeignNationalIds = (idType: IdentityTypeDirectors): IdItem[] => {
    const ids: IdItem[] = (application.directors?.directors || [])
      .filter((director) => director.idType === idType)
      .map(({ idNumber }) => ({
        idType: idType,
        idNumber: idNumber || '',
      }));

    if (application.contact?.identityType === idType) {
      ids.push({
        idType: idType,
        idNumber: application.contact.idNumber || '',
      });
    }

    return Array.from(new Set(ids));
  };

  const getPassportIds = (): IdItem[] => {
    const idHolders: IdItem[] = (application.directors?.directors || [])
      .filter(({ idType }) => idType === IdentityTypeDirectors.PASSPORT)
      .map((idHolder) => ({
        idType: idHolder.idType || IdentityTypeDirectors.PASSPORT,
        idNumber: stripWhitespace(idHolder.idNumber) || '',
      }));

    if (application.contact?.identityType === IdentityTypeDirectors.PASSPORT) {
      idHolders.push({
        idType: application.contact.identityType,
        idNumber: stripWhitespace(application.contact.idNumber) || '',
      });
    }

    return Array.from(new Set(idHolders));
  };

  const getPassports = (): IdItem[] => {
    const directorPassports: IdItem[] = (application.directors?.directors || [])
      .filter(({ idType }) => idType === IdentityTypeDirectors.PASSPORT)
      .map(({ idNumber }) => ({
        idType: IdentityTypeDirectors.PASSPORT,
        idNumber: idNumber || '',
      }));

    if (application.contact?.identityType === IdentityTypeDirectors.PASSPORT) {
      directorPassports.push({
        idType: IdentityTypeDirectors.PASSPORT,
        idNumber: application.contact?.idNumber || '',
      });
    }

    return Array.from(new Set(directorPassports));
  };

  const uploadDocs = async () => {
    setLoadingOverlayVisible(true);

    let docsToUpload: DocumentWithOptionalBody[] = [];

    if (imageJoinEnabled) {
      docsToUpload = await ImageService.joinDocsById(
        imageUploadState.documents.filter((doc) => !!doc.documentBody),
      );
    } else {
      docsToUpload = imageUploadState.documents.filter((doc) => !!doc.documentBody);
    }

    await salesforceService.saveDocuments(
      docsToUpload.map((doc) => {
        const { documentBody, ...document } = doc;
        return getDocumentApiFromOnboardingDocument(document, documentBody);
      }),
    );

    if (application.tapOnPhoneJourney) {
      OnboardingTealiumService.onDocumentUploadToPEnd();
    }

    /* BlueSky needs us to do a final save with the confirm application step name
       even though there is no associated screen with this step in POS revamp,
       it is needed to finalise the application on their side, as the onboarding
       journey for POS revamp currently reuses the same Salesforce code as is used
       for the existing "pre-vamp" onboarding journey.
     */
    const applicationApi: ApplicationAPI = {
      ...getApplicationAPIFromApplication(application, catalogue),
      stage: {
        stepName: StepNames.CONFIRM_APPLICATION,
      },
    };
    await salesforceService.saveApplication(applicationApi);

    const actions: ApplicationAction[] = [
      {
        type: ApplicationActionType.UPDATE_DOCUMENTS,
        documents: imageUploadState.documents.map((document) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { documentBody, ...rest } = document;
          return rest;
        }),
      },
      { type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP },
    ];

    setLoadingOverlayVisible(false);

    await dispatchAsync({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions,
    });
  };

  const isDocsValid = (uploadedDocs: DocumentWithOptionalBody[], setErrors?: boolean): boolean => {
    const hasAllIds = (): boolean => {
      const ids = getIDsAndPassports().map((id) => stripWhitespace(id.idNumber));

      const matches = ids.filter(
        (id) =>
          uploadedDocs.find(
            (doc) =>
              (doc.documentType === OnboardingDocumentTypeEnum.ID_CARD ||
                doc.documentType === OnboardingDocumentTypeEnum.ID_BOOK ||
                doc.documentType === OnboardingDocumentTypeEnum.PASSPORT) &&
              id === doc.idNumber,
          ) !== undefined,
      );

      return matches.length >= ids.length;
    };

    const hasAllIdTypes = (): boolean => {
      const requiredSmartCards = imageJoinEnabled ? 2 : 1;
      const hasRSAId = getIDsAndPassports().some(
        (id) => id.idType === IdentityTypeDirectors.RSA_ID,
      );
      if (!hasRSAId) return true;
      return imageUploadState.idDocsUploaded.every(
        (idDoc) =>
          idDoc.numOfSmartCards === requiredSmartCards ||
          idDoc.numOfGreenBooks === 1 ||
          idDoc.numOfPassports === 1,
      );
    };

    const hasAllWorkPermitFiles = (): boolean => {
      if (!foreignNationalsEnabled || getPassportIds().length === 0) {
        return true;
      }

      const ids = getPassportIds();

      const workPermitDocs = ids.filter(
        (idHolder) =>
          uploadedDocs.find(
            (doc) =>
              doc.documentType === OnboardingDocumentTypeEnum.WORK_PERMIT &&
              idHolder.idNumber === doc.idNumber,
          ) !== undefined,
      );
      return workPermitDocs.length == ids.length;
    };

    const hasAllPermanentResFiles = (): boolean => {
      if (!foreignNationalsEnabled || getPassportIds().length === 0) {
        return true;
      }

      const ids = getPassportIds();

      const permanentResDocs = ids.filter(
        (idHolder) =>
          uploadedDocs.find(
            (doc) =>
              doc.documentType === OnboardingDocumentTypeEnum.PERMANENT_RESIDENCY &&
              idHolder.idNumber === doc.idNumber,
          ) !== undefined,
      );
      return permanentResDocs.length == ids.length;
    };

    const hasFilesForDirectorTypeAndDocType = (
      directorType: IdentityTypeDirectors,
      foreignNationalType: ForeignNationalType,
      docType: string,
    ) => {
      if (!foreignNationalsEnabled || getForeignNationalIds(directorType).length === 0) {
        return true;
      }

      const ids = getForeignNationalIds(directorType).map((id) => stripWhitespace(id.idNumber));

      return (
        ids.filter(
          (id) =>
            imageUploadState.documents.find(
              (doc) => doc.documentType === docType && id === doc.idNumber,
            ) !== undefined,
        ).length >= ids.length
      );
    };

    const hasPOB = (): boolean =>
      uploadedDocs.find(
        ({ documentType }) => documentType === OnboardingDocumentTypeEnum.BANK_PROOF,
      ) !== undefined;

    const hasResLetter = (): boolean =>
      uploadedDocs.find(
        ({ documentType }) => documentType === OnboardingDocumentTypeEnum.RES_LETTER,
      ) !== undefined;

    const hasNPODoc = (): boolean =>
      uploadedDocs.find(
        ({ documentType }) => documentType === OnboardingDocumentTypeEnum.NPO_FOUNDING_DOC,
      ) !== undefined;

    const hasTrustDeed = (): boolean =>
      uploadedDocs.find(
        ({ documentType }) => documentType === OnboardingDocumentTypeEnum.TRUST_DEED,
      ) !== undefined;

    const hasTrustResLetter = (): boolean =>
      uploadedDocs.find(
        ({ documentType }) => documentType === OnboardingDocumentTypeEnum.TRUST_RES_LETTER,
      ) !== undefined;

    setUploadErrors({
      identityDocument: {
        error: !(hasAllIds() && hasAllIdTypes()) && !!setErrors,
        message: `Please upload all ID documents for the contact person${
          isCompanyTypeInList(companyType, [CompanyType.SOLE_PROP]) ? '.' : ' and all directors.'
        } ${imageJoinEnabled ? 'The back and front of the ID card is required.' : ''}`,
      },
      workPermit: {
        error: !hasAllWorkPermitFiles() && !!setErrors,
        message: `Please upload all work permit documents for the contact person ${
          isCompanyTypeInList(companyType, [CompanyType.SOLE_PROP]) ? '.' : ' and all directors.'
        }`,
      },
      permanentRes: {
        error: !hasAllPermanentResFiles() && !!setErrors,
        message: `Please upload all permanent residency documents for the contact person ${
          isCompanyTypeInList(companyType, [CompanyType.SOLE_PROP]) ? '.' : ' and all directors.'
        }`,
      },
      proofOfBanking: {
        error: !hasPOB() && !!setErrors,
        message: 'Please upload proof of banking documentation.',
      },
      resLetter: {
        error: !hasResLetter() && !!setErrors,
        message: 'Please upload resolution letter.',
      },
      refugeeLetter: {
        error:
          !hasFilesForDirectorTypeAndDocType(
            IdentityTypeDirectors.REFUGEE,
            ForeignNationalType.REFUGEE,
            OnboardingDocumentTypeEnum.REFUGEE_LETTER,
          ) && !!setErrors,
        message: 'Please upload refugee letter.',
      },
      asylumSeeker: {
        error:
          !hasFilesForDirectorTypeAndDocType(
            IdentityTypeDirectors.ASYLUM_SEEKER,
            ForeignNationalType.ASYLUM_SEEKER,
            OnboardingDocumentTypeEnum.ASYLUM_SEEKER,
          ) && !!setErrors,
        message: 'Please upload asylum seeker documentation.',
      },
      npoFoundingDoc: {
        error: !hasNPODoc() && !!setErrors,
        message: 'Please upload NPO founding documentation.',
      },
      trustDeed: {
        error: !hasTrustDeed() && !!setErrors,
        message: 'Please upload trust deed.',
      },
      trustResLetter: {
        error: !hasTrustResLetter() && !!setErrors,
        message: 'Please upload trust resolution letter.',
      },
    });

    let docsValid = false;

    switch (companyType) {
      case CompanyType.SOLE_PROP:
        docsValid = hasPOB();
        break;
      case CompanyType.PARTNERSHIP:
      case CompanyType.COMPANY:
      case CompanyType.CLOSE_CORP:
        docsValid = hasPOB() && hasResLetter();
        break;
      case CompanyType.NPO:
        docsValid = hasPOB() && hasResLetter() && hasNPODoc();
        break;
      case CompanyType.TRUST:
        docsValid = hasPOB() && hasTrustResLetter() && hasTrustDeed();
        break;
      default:
        docsValid = false;
    }

    if (foreignNationalsEnabled) {
      docsValid =
        docsValid &&
        hasAllPermanentResFiles() &&
        hasAllWorkPermitFiles() &&
        hasFilesForDirectorTypeAndDocType(
          IdentityTypeDirectors.REFUGEE,
          ForeignNationalType.REFUGEE,
          OnboardingDocumentTypeEnum.REFUGEE_LETTER,
        ) &&
        hasFilesForDirectorTypeAndDocType(
          IdentityTypeDirectors.ASYLUM_SEEKER,
          ForeignNationalType.ASYLUM_SEEKER,
          OnboardingDocumentTypeEnum.ASYLUM_SEEKER,
        );
    }

    return hasAllIds() && hasAllIdTypes() && docsValid;
  };

  const maxUploads = () => {
    return imageJoinEnabled ? getIDsAndPassports().length * 2 : getIDsAndPassports().length;
  };

  const handleNextOnClick = () => {
    if (!isDocsValid(imageUploadState.documents, true)) {
      return;
    }
    uploadDocs();
  };

  useEffect(() => {
    /* if somehow the user went back to the business details screen, changed
     the turnover, and managed to get back to this screen before the card fees call finished,
     show the loading screen until it finishes.
    */
    !isDpo && setLoadingOverlayVisible(catalogue.cardFees.status !== DataStatus.LOADED);
  }, [catalogue.cardFees.status]);

  useEffect(() => {
    imageUploadDispatch({
      type: ImageUploadActionType.INIT_DOCUMENTS,
      initialDocs:
        application.documents?.map((meta) => ({ ...meta, documentBody: '', initial: true })) || [],
      ids: getIDsAndPassports(),
      refugeeIds: getForeignNationalIds(IdentityTypeDirectors.REFUGEE),
      asylumIds: getForeignNationalIds(IdentityTypeDirectors.ASYLUM_SEEKER),
    });
  }, [application.documents]);

  useEffect(
    () => setOnNextEventHandler(handleNextOnClick),
    [imageUploadState.documents, application.documents],
  );

  useEffect(() => {
    if (imageUploadState.documents.length === 0) return;
    setNextButtonEnabled(isDocsValid(imageUploadState.documents));
  }, [imageUploadState.documents]);

  useEffect(() => {
    application.tapOnPhoneJourney
      ? OnboardingTealiumService.onDocumentUploadToPStart()
      : OnboardingTealiumService.onDocumentUploadStart(taggingJourney);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 0);
  }, []);

  const isCompanyTypeInList = (companyType: CompanyType, companyTypeList: CompanyType[]): boolean =>
    companyTypeList.includes(companyType);

  const getHintsFor = (type: string) => {
    const uploadDocUpdates = application.pendingApp?.pageUpdates?.find(
      (update) => update.stepName.toLowerCase() === PAGE_NAME.UPLOAD_DOCUMENTS,
    );

    return uploadDocUpdates?.fields.find(({ fieldName }) => fieldName === type)?.hints;
  };

  const idSubtext = () => {
    if (imageJoinEnabled) {
      return `For Smart ID please submit both front and back of the ID in JPEG format. For National Green book ID, upload the page with all ${
        isThirdPartyJourney ? 'the customers' : 'your personal'
      } details in JPEG format.`;
    }

    return `Submit only the front of the smart ID or the page with ${
      isThirdPartyJourney ? 'the customers' : 'your personal'
    } details if uploading ${
      isThirdPartyJourney ? 'the customers' : 'your'
    } National Green book ID or passport.`;
  };

  const config: DocumentConfig[] = [
    {
      props: {
        title: foreignNationalsEnabled ? 'SA ID / Passport' : 'Identity document',
        subtext: idSubtext(),
        boldedText:
          getIDsAndPassports().length > 1
            ? `Make sure you upload copies of ID documents for all the ${getBusinessTypeDirectorLookup(
                true,
                application.businessDetails?.companyType,
              )}s.`
            : '',
        fileLimitText: 'Maximum size: 1MB',
        documentType: OnboardingDocumentTypeEnum.ID_TYPE,
        idNumbers: getIDsAndPassports(),
        sizeLimit: ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg],
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_ID_Passport',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.ID_TYPE),
        maxFiles: maxUploads(),
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.identityDocument,
      },
      showOnlyIf: () => getIDsAndPassports().length > 0,
    },
    {
      props: {
        title: 'Refugee Letter',
        subtext: 'Please upload your Refugee Letter',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.REFUGEE_LETTER,
        idNumbers: getForeignNationalIds(IdentityTypeDirectors.REFUGEE),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG file type. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Refugee_Document',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.REFUGEE_LETTER),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.refugeeLetter,
      },
      showOnlyIf: () =>
        foreignNationalsEnabled && getForeignNationalIds(IdentityTypeDirectors.REFUGEE).length > 0,
    },
    {
      props: {
        title: 'Asylum Seeker Document',
        subtext: 'Please upload your Asylum seeker document',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.ASYLUM_SEEKER,
        idNumbers: getForeignNationalIds(IdentityTypeDirectors.ASYLUM_SEEKER),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG file type. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Onboarding_Upload_Documents_File_Upload_Asylum_Seeker_Document',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.ASYLUM_SEEKER),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.asylumSeeker,
      },
      showOnlyIf: () =>
        foreignNationalsEnabled &&
        getForeignNationalIds(IdentityTypeDirectors.ASYLUM_SEEKER).length > 0,
    },
    {
      props: {
        title: 'Work Permit',
        subtext: 'A clear copy of a valid work permit.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.WORK_PERMIT,
        passportNumbers: getPassportIds(),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg, FileTypes.pdf],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_Work_Permit',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.WORK_PERMIT),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.workPermit,
      },
      showOnlyIf: () => foreignNationalsEnabled && getPassportIds().length > 0,
    },
    {
      props: {
        title: 'Permanent Residency',
        subtext: 'A clear copy of your permanent residency document.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.PERMANENT_RESIDENCY,
        passportNumbers: getPassports(),
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg, FileTypes.pdf],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_Permanent_Residency',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.PERMANENT_RESIDENCY),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.permanentRes,
      },
      showOnlyIf: () => foreignNationalsEnabled && getPassports().length > 0,
    },
    {
      props: {
        title: 'Proof of banking',
        subtext: labels.bankingSubTitle,
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.BANK_PROOF,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg, FileTypes.pdf],
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_Proof_Of_Banking',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.BANK_PROOF),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.proofOfBanking,
      },
    },
    {
      props: {
        title: 'Resolution letter',
        subtext: 'A clear copy of your resolution letter.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.RES_LETTER,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg, FileTypes.pdf],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_Company_Resolution_Letter',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.RES_LETTER),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.resLetter,
      },
      showOnlyIf: () =>
        isCompanyTypeInList(companyType, [
          CompanyType.COMPANY,
          CompanyType.CLOSE_CORP,
          CompanyType.NPO,
          CompanyType.PARTNERSHIP,
        ]),
    },
    {
      props: {
        title: 'Trust Resolution letter',
        subtext: 'A clear copy of your trust resolution letter.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.TRUST_RES_LETTER,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg, FileTypes.pdf],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_Trust_Resolution_Letter',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.TRUST_RES_LETTER),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.trustResLetter,
      },
      showOnlyIf: () => isCompanyTypeInList(companyType, [CompanyType.TRUST]),
    },
    {
      props: {
        title: 'NPO founding documentation',
        subtext: 'A clear copy of your NPO founding documentation.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.NPO_FOUNDING_DOC,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg, FileTypes.pdf],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_NPO_Founding_Documentation',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.NPO_FOUNDING_DOC),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.npoFoundingDoc,
      },
      showOnlyIf: () => isCompanyTypeInList(companyType, [CompanyType.NPO]),
    },
    {
      props: {
        title: 'Trust deed',
        subtext: 'A clear copy of your trust deed.',
        fileLimitText: 'Maximum size: 3MB',
        documentType: OnboardingDocumentTypeEnum.TRUST_DEED,
        sizeLimit: NON_ID_SIZE_LIMIT,
        allowedFileTypes: [FileTypes.jpeg, FileTypes.pdf],
        unsupportedFileTypeErrorMessage:
          'Upload failed, We only support JPEG and PDF file types. Please try again.',
        setDeletedDocs: () => {},
        automationIdPrefix: 'Revamp_Upload_Documents_File_Upload_Trust_Deed',
        inMiniProgram: inMiniApp,
        isPending: isPendingApp,
        hints: getHintsFor(OnboardingDocumentTypeEnum.TRUST_DEED),
        maxFiles: 15,
        pageName: PAGE_NAME.UPLOAD_DOCUMENTS,
        error: uploadErrors.trustDeed,
      },
      showOnlyIf: () => isCompanyTypeInList(companyType, [CompanyType.TRUST]),
    },
  ];

  const getFileUploadComponents = () => {
    const filteredConfig = config.filter(
      (configItem) => !configItem.showOnlyIf || configItem.showOnlyIf(),
    );

    return filteredConfig.map((configItem, index) => {
      const props = {
        ...configItem.props,
        titleOriginal: configItem.props.title,
        title: `${index + 1}. ${configItem.props.title}`,
      };

      return <FileUpload lightMode={true} key={index} {...props} />;
    });
  };

  return (
    <>
      {imageUploadState.component && imageUploadState.showModal ? imageUploadState.component : null}
      <PageLayout>
        <HeadingLayout
          renderMainHeading={() => <HeadingOne>{labels.title}</HeadingOne>}
          renderSubHeading={() => <HeadingTwo>{labels.subTitle}</HeadingTwo>}
        />
        <section className="upload-documents-content-box">{getFileUploadComponents()}</section>
        <HelpButton
          onOpen={() =>
            OnboardingTealiumService.onHelpButtonActions(
              PAGE_NAME.UPLOAD_DOCUMENTS,
              'help',
              taggingJourney,
            )
          }
          onEmailClicked={() =>
            OnboardingTealiumService.onHelpButtonActions(
              PAGE_NAME.UPLOAD_DOCUMENTS,
              'email support',
              taggingJourney,
            )
          }
          onPhoneNumberClicked={() =>
            OnboardingTealiumService.onHelpButtonActions(
              PAGE_NAME.UPLOAD_DOCUMENTS,
              'call support number',
              taggingJourney,
            )
          }
        />
      </PageLayout>
    </>
  );
};
